<script setup>
import { ref, watch, onBeforeUnmount, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Fuse from 'fuse.js';
import CannedResponseList from './CannedResponseList.vue';
import CannedResponsePreview from './CannedResponsePreview.vue';
import { createFuseWorker } from './createFuseWorker.js';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['insert', 'close', 'update:show']);

const { t } = useI18n();
const store = useStore();

const searchQuery = ref('');
const selectedResponse = ref(null);
const allResponses = ref([]);
const filteredResponses = ref([]);
const isLoading = ref(false);
const searchInputRef = ref(null);
const activeFilter = ref('all');

// ---------------------------------------------------------------------------
// Fuse.js search — off the main thread via Web Worker, with main-thread
// fallback if worker creation fails (e.g. CSP restrictions).
// ---------------------------------------------------------------------------
const FUSE_OPTIONS = {
  keys: [
    { name: 'short_code', weight: 0.6 },
    { name: 'content', weight: 0.4 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  includeScore: true,
};

let worker = null;
let useWorker = false;
let fuseIndex = null; // main-thread fallback only
let workerReadyTimeout = null;

// --- Main-thread fallback ---
const mainThreadSearch = () => {
  let list = allResponses.value;

  if (activeFilter.value === 'favorites') {
    list = list.filter(r => r.is_favorite);
  }

  if (searchQuery.value.trim()) {
    const fuse =
      activeFilter.value === 'favorites'
        ? new Fuse(list, FUSE_OPTIONS)
        : fuseIndex;
    filteredResponses.value = fuse
      .search(searchQuery.value.trim())
      .map(r => r.item);
    return;
  }

  if (activeFilter.value === 'all') {
    filteredResponses.value = [...list].sort((a, b) => {
      if (a.is_favorite && !b.is_favorite) return -1;
      if (!a.is_favorite && b.is_favorite) return 1;
      return 0;
    });
    return;
  }

  filteredResponses.value = list;
};

// --- Worker path ---
const sendDataToWorker = () => {
  // toRaw() strips Vue's reactive Proxy wrappers so data can be structurally cloned
  const rawData = toRaw(allResponses.value).map(r => ({ ...r }));
  worker?.postMessage({ type: 'setData', payload: rawData });
  // If worker doesn't respond within 500ms, fall back to main thread
  clearTimeout(workerReadyTimeout);
  workerReadyTimeout = setTimeout(() => {
    if (filteredResponses.value.length === 0 && allResponses.value.length > 0) {
      useWorker = false;
      worker?.terminate();
      worker = null;
      fuseIndex = new Fuse(allResponses.value, FUSE_OPTIONS);
      mainThreadSearch();
    }
  }, 500);
};

const requestSearch = () => {
  worker?.postMessage({
    type: 'search',
    payload: { query: searchQuery.value, filter: activeFilter.value },
  });
};

// Try to create a Web Worker. Vite bundles the file + its deps (fuse.js)
// into an IIFE when type is omitted (not 'module').
try {
  worker = createFuseWorker();
  useWorker = true;

  worker.onmessage = ({ data }) => {
    if (data.type === 'results') {
      filteredResponses.value = data.payload;
    }
    if (data.type === 'ready') {
      clearTimeout(workerReadyTimeout);
      requestSearch();
    }
  };

  worker.onerror = () => {
    // Worker failed at runtime — fall back to main thread
    useWorker = false;
    worker = null;
    mainThreadSearch();
  };
} catch {
  useWorker = false;
}

onBeforeUnmount(() => {
  clearTimeout(workerReadyTimeout);
  worker?.terminate();
});

// --- Unified dispatch ---
const scheduleSearch = () => {
  if (useWorker) {
    requestSearch();
  } else {
    mainThreadSearch();
  }
};

watch(searchQuery, () => scheduleSearch());
watch(activeFilter, () => scheduleSearch());

let indexReady = false;

const buildIndex = () => {
  allResponses.value = store.getters.getCannedResponses;
  if (useWorker) {
    sendDataToWorker();
  } else {
    fuseIndex = new Fuse(allResponses.value, FUSE_OPTIONS);
  }
  indexReady = true;
};

// Build the fuzzy index as soon as the store has data — before the modal opens.
// ReplyBox preloads canned responses on mount, so the index is typically ready
// by the time the user clicks the lightning-bolt button.
watch(
  () => store.getters.getCannedResponses,
  cached => {
    if (cached && cached.length > 0) {
      buildIndex();
    }
  },
  { immediate: true }
);

watch(
  () => props.show,
  async val => {
    if (val) {
      searchQuery.value = '';
      selectedResponse.value = null;
      activeFilter.value = 'all';

      if (indexReady) {
        // Index already built in the background — just run the initial search
        scheduleSearch();
      } else {
        // Fallback: fetch from server if store was empty
        isLoading.value = true;
        try {
          await store.dispatch('getCannedResponse', {});
          buildIndex();
          scheduleSearch();
        } catch {
          allResponses.value = [];
          filteredResponses.value = [];
        } finally {
          isLoading.value = false;
        }
      }
      setTimeout(() => searchInputRef.value?.focus(), 100);
    }
  }
);

const onSelect = response => {
  selectedResponse.value = response;
};

const onUse = response => {
  emit('insert', response);
};

const useSelected = () => {
  if (selectedResponse.value) {
    emit('insert', selectedResponse.value);
  }
};

const onClose = () => {
  emit('close');
};

const onToggleFavorite = async response => {
  const newVal = !response.is_favorite;
  const idx = allResponses.value.findIndex(r => r.id === response.id);
  if (idx !== -1) {
    allResponses.value[idx] = {
      ...allResponses.value[idx],
      is_favorite: newVal,
    };
  }
  if (selectedResponse.value?.id === response.id) {
    selectedResponse.value = {
      ...selectedResponse.value,
      is_favorite: newVal,
    };
  }

  if (useWorker) {
    sendDataToWorker();
  } else {
    fuseIndex = new Fuse(allResponses.value, FUSE_OPTIONS);
    mainThreadSearch();
  }

  try {
    await store.dispatch('toggleFavorite', {
      id: response.id,
      isFavorite: newVal,
    });
  } catch {
    if (idx !== -1) {
      allResponses.value[idx] = {
        ...allResponses.value[idx],
        is_favorite: !newVal,
      };
    }
    if (useWorker) {
      sendDataToWorker();
    } else {
      fuseIndex = new Fuse(allResponses.value, FUSE_OPTIONS);
      mainThreadSearch();
    }
  }
};

const localShow = {
  get: () => props.show,
  set: val => emit('update:show', val),
};

const handleKeydown = event => {
  if (!props.show) return;

  const list = filteredResponses.value;
  if (!list.length) return;

  const currentIndex = list.findIndex(r => r.id === selectedResponse.value?.id);

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    const next = currentIndex < list.length - 1 ? currentIndex + 1 : 0;
    selectedResponse.value = list[next];
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    const prev = currentIndex > 0 ? currentIndex - 1 : list.length - 1;
    selectedResponse.value = list[prev];
  } else if (event.key === 'Enter' && selectedResponse.value) {
    event.preventDefault();
    onUse(selectedResponse.value);
  }
};
</script>

<template>
  <woot-modal
    :show="localShow.get()"
    :on-close="onClose"
    size="modal-xl"
    @update:show="localShow.set($event)"
  >
    <woot-modal-header
      :header-title="t('CANNED_RESPONSE_BROWSER.TITLE')"
      :header-content="t('CANNED_RESPONSE_BROWSER.SUBTITLE')"
    />
    <div class="flex flex-col h-[calc(100vh-8rem)]" @keydown="handleKeydown">
      <div class="px-4 py-3 border-b border-n-weak">
        <div class="flex items-center gap-3">
          <div
            class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
          >
            <fluent-icon icon="search" class="text-n-slate-12" size="16" />
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="search"
              :placeholder="t('CANNED_RESPONSE_BROWSER.SEARCH_PLACEHOLDER')"
              class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
            />
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <button
              class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer"
              :class="
                activeFilter === 'all'
                  ? 'bg-n-alpha-3 text-n-slate-12'
                  : 'text-n-slate-10 hover:bg-n-alpha-2'
              "
              @click="activeFilter = 'all'"
            >
              {{ t('CANNED_RESPONSE_BROWSER.ALL') }}
            </button>
            <button
              class="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer"
              :class="
                activeFilter === 'favorites'
                  ? 'bg-kpt-amber-soft text-kpt-amber'
                  : 'text-n-slate-10 hover:bg-n-alpha-2'
              "
              @click="activeFilter = 'favorites'"
            >
              <span
                class="i-lucide-star size-3.5"
                :style="
                  activeFilter === 'favorites' ? 'fill: currentColor' : ''
                "
              />
              {{ t('CANNED_RESPONSE_BROWSER.FAVORITES') }}
            </button>
          </div>
        </div>
      </div>
      <div class="flex flex-1 min-h-0">
        <CannedResponseList
          :responses="filteredResponses"
          :selected-id="selectedResponse?.id"
          :is-loading="isLoading"
          :search-query="searchQuery"
          :active-filter="activeFilter"
          @select="onSelect"
          @use="onUse"
          @toggle-favorite="onToggleFavorite"
        />
        <CannedResponsePreview :response="selectedResponse" />
      </div>
      <div
        class="flex items-center justify-end gap-3 px-4 py-3 border-t border-n-weak"
      >
        <woot-button variant="clear" @click="onClose">
          {{ t('CANNED_RESPONSE_BROWSER.CANCEL') }}
        </woot-button>
        <button
          class="rounded-xl px-4 py-2 text-sm font-medium text-white transition-colors"
          :class="
            selectedResponse
              ? 'bg-kpt-emerald hover:opacity-90 cursor-pointer'
              : 'bg-n-alpha-3 text-n-slate-10 cursor-not-allowed'
          "
          :disabled="!selectedResponse"
          @click="useSelected"
        >
          {{ t('CANNED_RESPONSE_BROWSER.USE_RESPONSE') }}
        </button>
      </div>
    </div>
  </woot-modal>
</template>
